<script setup lang="ts">
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import CommunityBottomSheet from '@/components/common/CommunityBottomSheet.vue'
import CommunitySelect from '@/components/posts/livePostView/RoomPost/component/CommunitySelect.vue'
import { GoLiveState } from '@/composition/goLive'
import { RoomControlBarInstance, RoomView } from '@ghl-revex/assets-drm-client'
import {
  Camera01Icon,
  ChevronLeftIcon,
  Microphone01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UICheckbox,
  UIDivider,
  UIFormItem,
  UIInput,
  UITextLgMedium,
  UITextMdMedium,
  UITextSmRegular,
} from '@gohighlevel/ghl-ui'
import { computed, onMounted, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const openDescriptionModal = ref(false)
const showDeviceSelection = ref(false)
const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
  updateFormData: {
    type: Function,
    required: true,
  },
  handleGoLive: {
    type: Function,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  roomControlBarRef: {
    type: Object as PropType<RoomControlBarInstance | null>,
    required: false,
    default: null,
  },
  roomViewRef: {
    type: Object as PropType<InstanceType<typeof RoomView> | null>,
    required: false,
    default: null,
  },
})
const { t } = useI18n()
const description = ref(props.formData.description)
const title = ref(props.formData.title)
const shouldSaveAsPost = ref(props.formData.shouldSaveAsPost)
const shouldNotifyMembers = ref(props.formData.shouldNotifyMembers)

// Device selection state
const selectedCamera = computed<string>(() => {
  return props.roomControlBarRef?.selectedCamera ?? ''
})
const cameraOptions = computed(() => {
  return (
    props.roomControlBarRef?.videoDevices.map(device => ({
      label: device.label,
      value: device.deviceId,
    })) ?? []
  )
})

const selectedMicrophone = computed<string>(() => {
  return props.roomControlBarRef?.selectedMicrophone ?? ''
})
const microphoneOptions = computed(() => {
  return (
    props.roomControlBarRef?.audioInputDevices.map(device => ({
      label: device.label,
      value: device.deviceId,
    })) ?? []
  )
})

const selectedSpeaker = computed<string>(() => {
  return props.roomControlBarRef?.selectedSpeaker ?? ''
})
const speakerOptions = computed(() => {
  return (
    props.roomControlBarRef?.audioOutputDevices.map(device => ({
      label: device.label,
      value: device.deviceId,
    })) ?? []
  )
})
const isValid = computed(() => {
  return title.value && description.value
})

const isDeviceSelectionValid = computed(() => {
  return selectedCamera.value && selectedMicrophone.value
})

const savePostDetails = () => {
  props.updateFormData('shouldSaveAsPost', shouldSaveAsPost.value)
  props.updateFormData('shouldNotifyMembers', shouldNotifyMembers.value)
  props.updateFormData('description', description.value)
  props.updateFormData('title', title.value)
  showDeviceSelection.value = true
}

const handleGoLive = () => {
  props.handleGoLive()
}

const openDescription = () => {
  openDescriptionModal.value = true
}

const saveDeviceSelection = () => {
  // Save device selections to form data
  console.log('selectedCamera', selectedCamera.value)
  console.log('selectedMicrophone', selectedMicrophone.value)
  console.log('selectedSpeaker', selectedSpeaker.value)
  GoLiveState.setCameraId(selectedCamera.value)
  GoLiveState.setMicrophoneId(selectedMicrophone.value)
  GoLiveState.setSpeakerId(selectedSpeaker.value)
  openDescriptionModal.value = false
}

const goBackToPostDetails = () => {
  showDeviceSelection.value = false
}

onMounted(() => {
  openDescriptionModal.value = true
})
async function handleMicrophoneChange(value: string) {
  await props.roomControlBarRef?.handleDeviceChange('microphone', value)
}
async function handleCameraChange(value: string) {
  await props.roomControlBarRef?.handleDeviceChange('camera', value)
}
const handleSpeakerChange = async (value: string) => {
  await props.roomControlBarRef?.handleDeviceChange('speaker', value)
}

const openMediaPermissionModal = () => {
  props.roomViewRef?.toggleShowPermissionModal(true)
}
</script>

<template>
  <div class="bg-communities-background/90 absolute bottom-10 z-40 flex w-full">
    <div class="w-full flex-col items-center justify-center">
      <div
        class="mx-2 mb-2 text-white"
        tabindex="0"
        role="button"
        @click="openDescription"
        @keyup.enter="openDescription"
      >
        <div
          v-if="formData.description && formData.title"
          class="flex flex-col text-white"
        >
          <UITextMdMedium
            class="mb-2 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ formData.title }}
          </UITextMdMedium>
          <UITextSmRegular class="line-clamp-3">
            {{ formData.description }}
          </UITextSmRegular>
        </div>
        <div v-else>{{ t('golive.tapToAddDescription') }}</div>
      </div>
      <div class="flex w-full justify-center">
        <CommunityButton
          :id="`go-live-btn`"
          type="primary"
          size="medium"
          class="golive-btn w-[97%] normal-case"
          :disabled="!isValid || !isDeviceSelectionValid"
          :loading="isLoading"
          @click="handleGoLive"
        >
          {{ t('golive.golive') }}
        </CommunityButton>
      </div>
    </div>
  </div>
  <CommunityBottomSheet
    v-model="openDescriptionModal"
    :is-full-screen="false"
    :is-drag-handle="true"
    :is-scroll-lock="true"
    snap-point="450px"
    :is-backdrop="true"
  >
    <div class="relative overflow-hidden">
      <!-- Post Details Content -->
      <div
        class="px-2 pb-4 text-communities-font-primary transition-transform duration-300 ease-in-out"
        :class="showDeviceSelection ? '-translate-x-full' : 'translate-x-0'"
      >
        <UITextLgMedium
          class="fon mb-1 text-center text-xl text-communities-font-primary"
        >
          {{ t('golive.addPostDetails') }}
        </UITextLgMedium>
        <UIDivider />
        <UIFormItem :label="t('common.title')" path="title" class="text-lg">
          <UIInput
            id="post-title"
            v-model="title"
            :placeholder="t('golive.enterTitle')"
            :show-count="true"
            :maxlength="100"
          />
        </UIFormItem>
        <UIFormItem
          :label="t('common.description')"
          path="description"
          class="grid"
        >
          <UIInput
            id="post-description"
            v-model="description"
            :placeholder="t('common.enterDescription')"
            type="textarea"
          />
        </UIFormItem>
        <div class="mb-4 grid gap-2">
          <UICheckbox
            id="notify-members-checkbox"
            :checked="shouldSaveAsPost"
            :label="t('golive.saveAsPost')"
            class="text-sm text-communities-font-primary"
            @update:checked="checked => (shouldSaveAsPost = checked)"
          />
          <UICheckbox
            id="notify-members-checkbox"
            :checked="shouldNotifyMembers"
            :label="t('golive.notifyMembersMobile')"
            class="text-sm text-communities-font-primary"
            @update:checked="checked => (shouldNotifyMembers = checked)"
          />
        </div>
        <CommunityButton
          :id="`save-post-details-btn`"
          type="primary"
          size="medium"
          class="w-full"
          :disabled="!isValid"
          @click="savePostDetails"
        >
          {{ t('common.next') }}
        </CommunityButton>
      </div>

      <!-- Device Selection Content -->
      <div
        class="absolute inset-0 px-2 pb-4 text-communities-font-primary transition-transform duration-300 ease-in-out"
        :class="showDeviceSelection ? 'translate-x-0' : 'translate-x-full'"
      >
        <div class="mb-1 flex items-center">
          <button
            class="mr-2 flex h-8 w-8 items-center justify-center rounded-full border-none text-communities-font-primary"
            @click="goBackToPostDetails"
          >
            <ChevronLeftIcon class="h-5 w-5" />
          </button>
          <UITextLgMedium
            class="flex-1 text-center text-xl text-communities-font-primary"
          >
            {{ t('golive.chooseMicCam') }}
          </UITextLgMedium>
          <div class="w-8" />
          <!-- Spacer for centering -->
        </div>
        <UIDivider />
        <div class="mx-auto w-[94%]">
          <!-- Camera Selection -->
          <UIFormItem :label="t('golive.selectCamera')" class="text-lg">
            <CommunitySelect
              v-if="cameraOptions.length > 0"
              id="camera-select"
              :model-value="selectedCamera"
              :options="cameraOptions"
              :placeholder="t('golive.selectMediaSource')"
              :icon="Camera01Icon"
              :label="t('common.ariaLabels.camera')"
              @change="handleCameraChange"
            />
            <div
              v-else
              class="flex h-10 flex-1 items-center rounded-full border border-solid border-[#5f6368] border-communities-border pl-2"
              tabindex="0"
              role="button"
              @click="openMediaPermissionModal"
              @keydown.enter="openMediaPermissionModal"
            >
              <UITextSmRegular class="pl-2 text-communities-font-primary">
                {{ t('golive.needCameraPermission') }}
              </UITextSmRegular>
            </div>
          </UIFormItem>

          <!-- Microphone Selection -->
          <UIFormItem :label="t('golive.selectMicrophone')" class="text-lg">
            <CommunitySelect
              v-if="microphoneOptions.length > 0"
              id="microphone-select"
              v-model="selectedMicrophone"
              :options="microphoneOptions"
              :placeholder="t('golive.selectMicrophone')"
              :icon="Microphone01Icon"
              :label="t('common.ariaLabels.microphone')"
              @change="handleMicrophoneChange"
            />
            <div
              v-else
              class="flex h-10 flex-1 items-center rounded-full border border-solid border-[#5f6368] border-communities-border pl-2"
              tabindex="0"
              role="button"
              @click="openMediaPermissionModal"
              @keydown.enter="openMediaPermissionModal"
            >
              <UITextSmRegular class="pl-2 text-communities-font-primary">
                {{ t('golive.needMicrophonePermission') }}
              </UITextSmRegular>
            </div>
          </UIFormItem>

          <!-- Speaker Selection -->
          <!-- <UIFormItem :label="t('golive.selectSpeaker')" class="text-lg">
            <CommunitySelect
              v-if="speakerOptions.length > 0"
              id="live-preview-speaker-select"
              :model-value="selectedSpeaker"
              :options="speakerOptions"
              :placeholder="t('golive.selectSpeaker')"
              :icon="VolumeMaxIcon"
              label="Speaker"
              @change="handleSpeakerChange"
            />
            <div
              v-else
              class="flex h-10 flex-1 items-center rounded-full border border-solid border-[#5f6368] border-communities-border pl-2"
              tabindex="0"
              role="button"
              @click="openMediaPermissionModal"
              @keydown.enter="openMediaPermissionModal"
            >
              <UITextSmRegular class="pl-2 text-communities-font-primary">
                Need Speaker Permission
              </UITextSmRegular>
            </div>
          </UIFormItem> -->
        </div>
        <CommunityButton
          :id="`save-device-selection-btn`"
          type="primary"
          size="medium"
          class="w-full"
          :disabled="!isDeviceSelectionValid"
          @click="saveDeviceSelection"
        >
          {{ t('common.done') }}
        </CommunityButton>
      </div>
    </div>
  </CommunityBottomSheet>
</template>

<style scoped>
.n-divider {
  margin: 0px !important;
  margin-bottom: 24px !important;
}
.n-form-item-label .n-form-item-label__text {
  font-size: 16px !important;
}
.golive-btn {
  background-color: white !important;
  color: #344054 !important;
}

:deep(#camera-select) {
  color: black !important;
}
:deep(#microphone-select) {
  color: black !important;
}
:deep(#live-preview-speaker-select) {
  color: black !important;
}
:deep(.community-select-arrow-icon) {
  color: black !important;
}
:deep(.community-select-icon) {
  color: black !important;
  top: 6px;
}
:deep(.community-select-arrow-icon) {
  top: 6px;
}
</style>
